/**
 * Quotes dashboard page (19.1) — spreadsheet-style table with pricing confidence.
 * Integrates CreateQuoteModal (19.2) and QuoteDetailView (19.3-19.5).
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchQuotes } from '../api/quotes';
import CreateQuoteModal from '../components/quotes/CreateQuoteModal';
import QuoteDetailView from '../components/quotes/QuoteDetailView';
import { Button, LoadingSpinner, EmptyState, SearchBar, Pagination } from '../components/common';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-yellow-100 text-yellow-700',
  converted: 'bg-purple-100 text-purple-700',
};

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'converted', label: 'Converted' },
];

function confidenceColor(pct) {
  if (pct >= 80) return 'text-green-600';
  if (pct >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function confidenceDot(pct) {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('-created_at');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        search: search || undefined,
        ordering: sortField,
      };
      if (statusFilter) params.quote_status = statusFilter;
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
      const resp = await fetchQuotes(params);
      const items = resp.data || resp.results || resp || [];
      setQuotes(Array.isArray(items) ? items : []);
      setTotalCount(resp.count || items.length);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortField]);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  function handleSort(field) {
    if (sortField === field) setSortField(`-${field}`);
    else if (sortField === `-${field}`) setSortField(field);
    else setSortField(field);
  }

  function SortIcon({ field }) {
    if (sortField === field) return <span className="ml-0.5">&#9650;</span>;
    if (sortField === `-${field}`) return <span className="ml-0.5">&#9660;</span>;
    return null;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-foreground">Quotes</h1>
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={() => setCreateOpen(true)}>+ New Quote</Button>
          <span className="text-sm text-gray-500">{totalCount} quote{totalCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              statusFilter === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto">
          <SearchBar value={search} onChange={setSearch} placeholder="Search quotes..." className="w-52" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><LoadingSpinner size="lg" /></div>
      ) : quotes.length === 0 ? (
        <EmptyState
          title={search || statusFilter ? 'No matching quotes' : 'No quotes yet'}
          description={search || statusFilter ? 'Try adjusting filters.' : 'Create your first quote to get started.'}
        />
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2 cursor-pointer hover:text-gray-700" onClick={() => handleSort('quote_number')}>
                    Quote #<SortIcon field="quote_number" />
                  </th>
                  <th className="px-3 py-2 cursor-pointer hover:text-gray-700" onClick={() => handleSort('customer')}>
                    Customer<SortIcon field="customer" />
                  </th>
                  <th className="px-3 py-2">Parent Item</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:text-gray-700" onClick={() => handleSort('total_sell_price')}>
                    Sell Price<SortIcon field="total_sell_price" />
                  </th>
                  <th className="px-3 py-2 text-right">Cost Est.</th>
                  <th className="px-3 py-2 text-right">Margin</th>
                  <th className="px-3 py-2 text-center cursor-pointer hover:text-gray-700" onClick={() => handleSort('pricing_confidence_pct')}>
                    Confidence<SortIcon field="pricing_confidence_pct" />
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer hover:text-gray-700" onClick={() => handleSort('quote_status')}>
                    Status<SortIcon field="quote_status" />
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:text-gray-700" onClick={() => handleSort('created_at')}>
                    Created<SortIcon field="created_at" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {quotes.map((q) => {
                  const confPct = q.pricing_confidence_pct ?? 0;
                  return (
                    <tr
                      key={q.id}
                      onClick={() => setDetailId(q.id)}
                      className="hover:bg-blue-50 cursor-pointer"
                    >
                      <td className="px-3 py-2 font-medium text-foreground">
                        {q.quote_number}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {q.customer_name || q.customer || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {q.parent_item_display || q.parent_item || '—'}
                      </td>
                      <td className="px-3 py-2 text-right">{q.quantity}</td>
                      <td className="px-3 py-2 text-right">{fmtCurrency(q.total_sell_price)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{fmtCurrency(q.estimated_parts_cost)}</td>
                      <td className="px-3 py-2 text-right">
                        {q.estimated_margin != null ? (
                          <span className={q.estimated_margin > 20 ? 'text-green-600' : q.estimated_margin > 0 ? 'text-yellow-600' : 'text-red-600'}>
                            {Number(q.estimated_margin).toFixed(1)}%
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <span className={`inline-block w-2 h-2 rounded-full ${confidenceDot(confPct)}`} />
                          <span className={`text-xs font-medium ${confidenceColor(confPct)}`}>
                            {confPct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[q.quote_status] || 'bg-gray-100 text-gray-600'}`}>
                          {q.quote_status}
                        </span>
                        {q.converted_to_job && (
                          <span className="ml-1 text-[10px] text-purple-600">JOB</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-400 text-xs">
                        {fmtDate(q.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <Pagination current={page} total={Math.ceil(totalCount / 25)} onChange={setPage} />
          </div>
        </>
      )}

      {/* Create quote modal */}
      <CreateQuoteModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(quote) => {
          loadQuotes();
          setDetailId(quote.id);
        }}
      />

      {/* Quote detail slide-out */}
      <QuoteDetailView
        quoteId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onRefreshList={loadQuotes}
      />
    </div>
  );
}
